/**
 * ProgressRing Component
 * ======================
 * SVG circular progress animation around the logo
 * Creates a premium golden animated progress indicator
 */

import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

const ProgressRing = ({ isAnimating }) => {
  const circleRef = useRef(null);
  const glowCircleRef = useRef(null);

  useEffect(() => {
    if (!isAnimating) return;

    const circle = circleRef.current;
    const glowCircle = glowCircleRef.current;

    // Circle properties
    const radius = 90;
    const circumference = 2 * Math.PI * radius;

    // Set initial state
    gsap.set([circle, glowCircle], {
      strokeDasharray: circumference,
      strokeDashoffset: circumference,
      rotation: -90,
      transformOrigin: 'center center',
    });

    const ctx = gsap.context(() => {
      const tl = gsap.timeline();

      // Animate the progress
      tl.to([circle, glowCircle], {
        strokeDashoffset: 0,
        duration: 2.2,
        ease: 'power1.inOut',
      });

      // Add rotation for dynamic feel
      tl.to(
        [circle, glowCircle],
        {
          rotation: 270,
          duration: 2.2,
          ease: 'power1.inOut',
        },
        0
      );

      // Pulse effect on glow
      gsap.to(glowCircle, {
        opacity: 0.5,
        duration: 0.5,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
      });
    });

    return () => ctx.revert();
  }, [isAnimating]);

  return (
    <svg
      className="progress-ring"
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Glow filter */}
      <defs>
        <filter id="progressGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="progressGoldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFD700" />
          <stop offset="50%" stopColor="#D4A853" />
          <stop offset="100%" stopColor="#B8860B" />
        </linearGradient>
      </defs>

      {/* Background track */}
      <circle
        cx="100"
        cy="100"
        r="90"
        fill="none"
        stroke="rgba(212, 168, 83, 0.1)"
        strokeWidth="2"
      />

      {/* Glow circle */}
      <circle
        ref={glowCircleRef}
        cx="100"
        cy="100"
        r="90"
        fill="none"
        stroke="#FFD700"
        strokeWidth="4"
        strokeLinecap="round"
        filter="url(#progressGlow)"
        opacity="0.6"
      />

      {/* Main progress circle */}
      <circle
        ref={circleRef}
        cx="100"
        cy="100"
        r="90"
        fill="none"
        stroke="url(#progressGoldGradient)"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
};

export default ProgressRing;
